import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, wsUrl, fetchIceServers } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck, Loader2, MapPin, Camera, Mic, MicOff, Lock, Send, MessageSquare, X,
  CheckCircle2, AlertCircle, Video, Flashlight, FlashlightOff,
} from "lucide-react";
import { toast } from "sonner";
import { createPeer, handleSignal } from "@/lib/webrtc";
import { debug } from "@/lib/debug";

const STEP = { LOADING: "l", WELCOME: "w", PERMISSIONS: "p", CALL: "c", ENDED: "e" };

export default function InsuredSession() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(STEP.LOADING);
  const [consent, setConsent] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [geo, setGeo] = useState(null);
  const [peritoCamOn, setPeritoCamOn] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [aseguradoMicOn, setAseguradoMicOn] = useState(true);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  // Estado de la conexión para feedback al usuario.
  // "idle" → aún no conectado; "connecting" → ICE en curso; "reconnecting" →
  // glitch detectado, reintentando; "connected" → todo OK; "audio_only" →
  // sin vídeo del asegurado (sin cámara o permiso denegado).
  const [connState, setConnState] = useState("idle");
  const [audioOnly, setAudioOnly] = useState(false);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peritoJoinedRef = useRef(false);
  const recorderRef = useRef(null);
  const recordingActiveRef = useRef(false);
  const geoRef = useRef(null);  // Espejo de `geo` para closures (doCapture lee siempre el último valor)
  const makeOfferRef = useRef(null);  // Para que el ICE-restart pueda renegociar
  const watchdogRef = useRef(null);   // Re-arma el watchdog que regenera offer si la pc no conecta en 10s
  const wsReconnectAttemptsRef = useRef(0);  // Contador para backoff exponencial

  useEffect(() => {
    api.get(`/session/${token}`)
      .then(r => { setInfo(r.data); setStep(r.data.consent_required ? STEP.WELCOME : STEP.PERMISSIONS); })
      .catch(e => { setError(e?.response?.data?.detail || "Sesión no encontrada"); setStep(STEP.ENDED); });
  }, [token]);

  // Cuando entramos en CALL y hay stream, asignar al <video> local y reproducir.
  // Hacemos esto en un useEffect porque el <video> sólo existe cuando step === CALL.
  useEffect(() => {
    if (step !== STEP.CALL) return;
    const tryAssign = () => {
      const v = localVideoRef.current;
      const s = localStreamRef.current;
      if (!v || !s) return false;
      if (v.srcObject !== s) v.srcObject = s;
      v.muted = true;
      v.playsInline = true;
      const playPromise = v.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch((e) => console.warn("[asegurado] local play retry", e?.name || e));
      }
      return true;
    };
    if (!tryAssign()) {
      // reintentar en próximos frames hasta que el video esté listo
      let attempts = 0;
      const iv = setInterval(() => {
        attempts++;
        if (tryAssign() || attempts > 20) clearInterval(iv);
      }, 100);
      return () => clearInterval(iv);
    }
  }, [step]);

  const acceptConsent = async () => {
    if (!consent) return;
    setAccepting(true);
    try {
      await api.post(`/session/${token}/consent`, { accepted: true, user_agent: navigator.userAgent });
      setStep(STEP.PERMISSIONS);
    } catch (e) {
      toast.error("No se pudo registrar el consentimiento");
    } finally { setAccepting(false); }
  };

  const startSession = async () => {
    // ICE servers
    const iceServers = await fetchIceServers();

    // Helper para avisar al perito de incidencias de permisos.
    // No-throw: si el endpoint falla, simplemente loggeamos.
    const reportPermissionIssue = async (payload) => {
      try {
        await api.post(`/session/${token}/permission-issue`, {
          camera_denied: false,
          microphone_denied: false,
          geolocation_denied: false,
          kind: "",
          message: "",
          user_agent: navigator.userAgent || "",
          ...payload,
        });
      } catch (e) {
        console.warn("[permission-issue] no se pudo notificar al perito", e);
      }
    };

    // Geolocation — estrategia en 2 fases para que funcione bien con WiFi (iOS Safari).
    // Fase 1: ubicación aproximada rápida (WiFi positioning, ~6s, sin GPS).
    // Fase 2: watchPosition con alta precisión, refina hacia GPS cuando esté disponible.
    if (navigator.geolocation) {
      const onSuccess = (p, label) => {
        const g = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
        geoRef.current = g;
        setGeo(g);
        if (label === "init") toast.success("Ubicación activada");
      };
      const errorMsg = (err) => {
        if (err.code === 1) return "Has denegado el permiso de ubicación. Las fotos se enviarán sin ubicación. Puedes activarlo en Ajustes del navegador.";
        if (err.code === 2) return "Ubicación no disponible. Verifica que el GPS/WiFi esté activado.";
        return "No hemos podido obtener tu ubicación. Inténtalo de nuevo o continúa sin ella.";
      };
      let initOK = false;
      const tryQuick = (attempt = 1) => {
        navigator.geolocation.getCurrentPosition(
          (p) => { initOK = true; onSuccess(p, "init"); },
          (err) => {
            console.warn(`[geo] init attempt ${attempt} error`, err.code, err.message);
            if (err.code !== 1 && attempt < 2) {
              setTimeout(() => tryQuick(attempt + 1), 1500);
            } else if (!initOK) {
              toast.warning(errorMsg(err));
              // Solo notificamos al perito si fue DENEGADO (code=1). Los timeouts y
              // unavailable son temporales y el perito no puede hacer nada por teléfono.
              if (err.code === 1) {
                reportPermissionIssue({
                  geolocation_denied: true,
                  kind: "GeoPermissionDenied",
                  message: "El asegurado ha denegado el permiso de ubicación.",
                });
              }
            }
          },
          // enableHighAccuracy:false usa WiFi positioning de iOS → rápido y funciona en interiores
          { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 }
        );
      };
      tryQuick();
      try {
        const wid = navigator.geolocation.watchPosition(
          (p) => onSuccess(p, "watch"),
          (err) => console.warn("[geo] watch error", err.code, err.message),
          { enableHighAccuracy: true, maximumAge: 3000, timeout: 27000 }
        );
        window.__vp_geo_watch = wid;
      } catch (e) { console.warn("[geo] watchPosition unavailable", e); }
    } else {
      toast.warning("Tu navegador no soporta geolocalización.");
      reportPermissionIssue({
        geolocation_denied: true,
        kind: "GeoNotSupported",
        message: "El navegador del asegurado no soporta geolocalización.",
      });
    }
    // Camera (rear) + mic. En caso de fallo total, FALLBACK A SOLO AUDIO para
    // que la videoperitación pueda seguir aunque sea como llamada de voz —
    // mejor algo que nada. Si el mic también falla, ahí sí abortamos.
    let stream;
    let videoEnabled = false;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });
        videoEnabled = stream.getVideoTracks().length > 0;
      } catch (_) {
        // Fallback any camera
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          videoEnabled = stream.getVideoTracks().length > 0;
        } catch (_) {
          // Último fallback: solo audio. Mejor llamada de voz que nada.
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          videoEnabled = false;
          setAudioOnly(true);
          toast.warning("Sin cámara: la sesión continuará solo con audio. El perito te guiará por voz.");
          // Notificamos al perito para que sepa que solo recibe audio.
          await reportPermissionIssue({
            camera_denied: true,
            kind: "CameraUnavailable",
            message: "Cámara no disponible — fallback a solo audio.",
          });
        }
      }
    } catch (e) {
      console.error("[media] getUserMedia failed", e?.name, e?.message);
      // Mic tampoco: aislamos qué falla para informar al perito.
      let camDenied = false, micDenied = false;
      let camKind = "", micKind = "";
      try {
        const s1 = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        s1.getTracks().forEach((t) => t.stop());
      } catch (e1) { camDenied = true; camKind = e1?.name || "unknown"; }
      try {
        const s2 = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
        s2.getTracks().forEach((t) => t.stop());
      } catch (e2) { micDenied = true; micKind = e2?.name || "unknown"; }
      const parts = [];
      if (camDenied) parts.push("cámara");
      if (micDenied) parts.push("micrófono");
      toast.error(
        parts.length
          ? `No podemos acceder a tu ${parts.join(" y ")}. El perito recibirá un aviso y te llamará por teléfono.`
          : "No podemos acceder a la cámara y al micrófono. El perito recibirá un aviso y te llamará por teléfono."
      );
      await reportPermissionIssue({
        camera_denied: camDenied || !micDenied,
        microphone_denied: micDenied || !camDenied,
        kind: camKind || micKind || e?.name || "MediaError",
        message: (e?.message || "") + (camKind ? ` cam:${camKind}` : "") + (micKind ? ` mic:${micKind}` : ""),
      });
      return;
    }
    localStreamRef.current = stream;
    setStep(STEP.CALL);
    setConnState("connecting");
    startTranscription(stream);
    // Detectar soporte de linterna en la pista de vídeo actual.
    // Solo disponible en Chrome/Android y Safari iOS recientes con cámara trasera.
    let torchSupportedLocal = false;
    try {
      const vt = stream.getVideoTracks()[0];
      const caps = vt && typeof vt.getCapabilities === "function" ? vt.getCapabilities() : null;
      if (caps && "torch" in caps) {
        torchSupportedLocal = true;
        setTorchSupported(true);
      }
    } catch (_) { /* ignore */ }
    // El <video> local se asigna vía useEffect cuando step === CALL (ver más abajo)

    // WS + PC. Encapsulamos la conexión WS en una función para poder
    // reconectarla con backoff si se cae (cambio WiFi↔4G, pérdida de señal).
    const pc = createPeer({
      iceServers,
      sendSignal: (payload) => {
        const w = wsRef.current;
        if (w && w.readyState === WebSocket.OPEN) {
          w.send(JSON.stringify({ type: "signal", payload }));
        }
      },
      onTrack: (s) => {
        debug("[asegurado] remote track received");
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = s;
          remoteVideoRef.current.play().catch(() => { /* autoplay */ });
        }
      },
      onState: (st, ice) => {
        debug("[asegurado] pc state", st, "ice:", ice);
        if (st === "connected" || ice === "connected" || ice === "completed") {
          setConnState("connected");
        } else if (ice === "checking" || st === "connecting") {
          setConnState("connecting");
        } else if (ice === "disconnected" || st === "disconnected") {
          setConnState("reconnecting");
        } else if (ice === "failed" || st === "failed") {
          setConnState("reconnecting");
        }
      },
      onRestartNeeded: () => {
        // El helper ya llamó a restartIce(); ahora generamos nueva offer.
        if (peritoJoinedRef.current && makeOfferRef.current) {
          makeOfferRef.current({ iceRestart: true });
        }
      },
    });
    pcRef.current = pc;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));

    const makeOffer = async (opts = {}) => {
      try {
        debug("[asegurado] creating offer", opts.iceRestart ? "(ICE restart)" : "");
        const offer = await pc.createOffer({
          offerToReceiveVideo: true,
          offerToReceiveAudio: true,
          iceRestart: !!opts.iceRestart,
        });
        await pc.setLocalDescription(offer);
        const w = wsRef.current;
        if (w && w.readyState === WebSocket.OPEN) {
          w.send(JSON.stringify({ type: "signal", payload: { sdp: pc.localDescription } }));
        }
      } catch (e) { console.error("[asegurado] offer error", e); }
    };
    makeOfferRef.current = makeOffer;

    // Watchdog: si tras 10 s desde que el perito está presente la pc no llega a
    // 'connected', regeneramos la offer con iceRestart automáticamente. Esto cubre
    // cualquier edge-case (drop intermedio del WS justo después del primer offer,
    // candidatos ICE perdidos por NAT inestable, perito que recarga la página
    // entre nuestro setLocalDescription y la entrega del SDP). Sin esto, una offer
    // perdida deja al perito en "Esperando" indefinidamente.
    let watchdogTimer = null;
    const armWatchdog = () => {
      if (watchdogTimer) clearTimeout(watchdogTimer);
      watchdogTimer = setTimeout(async () => {
        if (pc.connectionState !== "connected" && peritoJoinedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
          debug("[asegurado] watchdog: pc no conectada tras 10s, regenerando offer (iceRestart)");
          await makeOffer({ iceRestart: true });
          armWatchdog();   // Re-armar para próximo ciclo
        }
      }, 10000);
    };
    watchdogRef.current = armWatchdog;

    const connectWs = () => {
      const ws = new WebSocket(wsUrl(token, "asegurado"));
      wsRef.current = ws;
      // Keepalive cada 25 s: el servidor responde con pong. Evita que
      // Cloudflare/WAF/proxies corten el WS por inactividad mientras se
      // espera al perito y dispare la cascada de reconexiones.
      let pingTimer = null;
      ws.onopen = () => {
        debug("[asegurado] WS open");
        wsReconnectAttemptsRef.current = 0;
        try {
          ws.send(JSON.stringify({ type: "torch_state", supported: torchSupportedLocal, on: torchOn }));
        } catch (_) { /* ignore */ }
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) { /* ignore */ }
          }
        }, 25000);
      };

      ws.onmessage = async (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        debug("[asegurado] WS msg:", msg.type, msg.role || "");
        if (msg.type === "peers") {
          // Al ABRIR el WS recibimos `peers` con quién hay ya en la sala.
          // - Primera vez (peritoJoinedRef=false): offer normal.
          // - Reconexión (peritoJoinedRef=true) tras un drop del WS del
          //   asegurado (cambio WiFi↔4G, fondo en iOS, etc.) Y la pc del
          //   asegurado todavía NO está conectada: el peer del perito sigue
          //   ahí, pero NO recibirá nuestra nueva oferta a menos que se la
          //   reenviemos con iceRestart. Si no hacemos esto, el perito ve
          //   "Esperando" eternamente aunque el asegurado siga en la sala.
          if (msg.present.includes("perito")) {
            const alreadyJoined = peritoJoinedRef.current;
            const pcConnected = pc.connectionState === "connected";
            peritoJoinedRef.current = true;
            if (!alreadyJoined) {
              await makeOffer();
            } else if (!pcConnected) {
              debug("[asegurado] WS reconectado, perito ya en sala pero pc no conectada → regenerar offer (iceRestart)");
              await makeOffer({ iceRestart: true });
            }
            // Arrancar watchdog para garantizar conexión en <10s.
            if (watchdogRef.current) watchdogRef.current();
          }
        } else if (msg.type === "peer_joined" && msg.role === "perito") {
          // Si el perito acaba de aparecer por PRIMERA vez → offer normal.
          // Si ya lo habíamos visto antes (peritoJoinedRef=true) y aún no hay
          // peerConnection conectada (caso típico: el perito recargó la página
          // o cambió de red), debemos REGENERAR la offer con iceRestart para
          // que la nueva pc del perito reciba la oferta y arranque la
          // negociación de vídeo. Si no lo hacemos, el perito ve "Esperando"
          // eternamente aunque el asegurado esté en la sala.
          const alreadyJoined = peritoJoinedRef.current;
          const pcConnected = pc.connectionState === "connected";
          peritoJoinedRef.current = true;
          if (!alreadyJoined) {
            await makeOffer();
          } else if (!pcConnected) {
            debug("[asegurado] perito re-joined, regenerando offer con iceRestart");
            await makeOffer({ iceRestart: true });
          }
          // Arrancar watchdog para garantizar conexión en <10s.
          if (watchdogRef.current) watchdogRef.current();
          try {
            ws.send(JSON.stringify({ type: "torch_state", supported: torchSupportedLocal, on: torchOn }));
          } catch (_) { /* ignore */ }
        } else if (msg.type === "peer_left" && msg.role === "perito") {
          peritoJoinedRef.current = false;
          setPeritoCamOn(false);
        } else if (msg.type === "request_offer_regen") {
          // El perito nos pide regenerar la offer (su lado no ha visto tracks
          // en X segundos aunque nosotros creemos estar conectados). Forzamos
          // iceRestart independientemente del estado local.
          debug("[asegurado] perito solicita regenerar offer → iceRestart");
          try {
            await makeOffer({ iceRestart: true });
            if (watchdogRef.current) watchdogRef.current();
          } catch (e) {
            debug("[asegurado] fallo regenerando offer solicitada por perito:", e);
          }
        } else if (msg.type === "signal") {
          await handleSignal(pc, msg.payload, {
            sendSignal: (p) => {
              const w = wsRef.current;
              if (w && w.readyState === WebSocket.OPEN) {
                w.send(JSON.stringify({ type: "signal", payload: p }));
              }
            },
          });
        } else if (msg.type === "chat") {
          setChat((c) => [...c, msg]);
        } else if (msg.type === "camera") {
          setPeritoCamOn(!!msg.on);
        } else if (msg.type === "torch") {
          await applyTorchFromPerito(!!msg.on);
        } else if (msg.type === "request_capture") {
          await doCapture();
        } else if (msg.type === "end_call") {
          endCall("El perito ha finalizado la sesión");
        }
      };
      ws.onerror = (e) => console.warn("[asegurado] WS error event:", e?.type, "readyState=", ws.readyState);
      ws.onclose = (ev) => {
        console.warn("[asegurado] WS closed", { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        // 4xxx = errores aplicativos (sesión finalizada, no reconectar).
        // 1000 = cierre normal por endCall().
        if (ev.code >= 4000 && ev.code < 5000) {
          // Mostrar al asegurado el motivo concreto, no un "Reconectando" genérico.
          let userMsg = "";
          if (ev.code === 4004) userMsg = "Esta videoperitación ya no existe.";
          else if (ev.code === 4010) userMsg = "La videoperitación ha sido cerrada por el perito.";
          else userMsg = `Conexión rechazada por el servidor (${ev.code}${ev.reason ? " - " + ev.reason : ""}).`;
          toast.error(userMsg, { duration: 20000 });
          setConnState("idle");
          return;
        }
        if (ev.code === 1000) return;
        // Reconectamos con backoff exponencial 1s, 2s, 4s, 8s, máx 15s, 8 intentos.
        const attempts = ++wsReconnectAttemptsRef.current;
        if (attempts > 8) {
          toast.error("No se pudo restablecer la conexión. Recarga la página.");
          return;
        }
        const delay = Math.min(15000, 1000 * Math.pow(2, attempts - 1));
        setConnState("reconnecting");
        debug(`[asegurado] WS reconnect in ${delay} ms (intento ${attempts})`);
        setTimeout(connectWs, delay);
      };
    };
    connectWs();
  };

  const doCapture = async () => {
    const video = localVideoRef.current;
    const stream = localStreamRef.current;
    if (!stream) return;
    try {
      const track = stream.getVideoTracks()[0];
      if (!track) return;

      // Resolución objetivo: máx 1600 px lado largo (~1600×1200 = 1.9 MP).
      // Calidad sobrada para peritación y ~80% más ligera que la nativa del sensor.
      const MAX_SIDE = 1600;

      let bitmap = null;
      // 1) ImageCapture.grabFrame (Chrome/Android) — toma un frame del stream activo,
      //    no dispara la cámara a resolución máxima del sensor (que tarda 1-3 s).
      if (typeof window.ImageCapture !== "undefined") {
        try {
          const ic = new window.ImageCapture(track);
          bitmap = await ic.grabFrame();
        } catch (e) {
          console.warn("[capture] grabFrame failed, fallback canvas", e);
        }
      }

      // 2) Fallback: <video> -> canvas (iOS Safari y resto).
      if (!bitmap) {
        if (video) { try { await video.play(); } catch (_) { /* autoplay restricted */ } }
        for (let i = 0; i < 10 && (!video || video.readyState < 2 || video.videoWidth === 0); i++) {
          await new Promise(r => setTimeout(r, 100));
        }
      }

      const srcW = bitmap?.width || video?.videoWidth || 1280;
      const srcH = bitmap?.height || video?.videoHeight || 720;
      const scale = Math.min(1, MAX_SIDE / Math.max(srcW, srcH));
      const w = Math.round(srcW * scale);
      const h = Math.round(srcH * scale);

      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (bitmap) {
        ctx.drawImage(bitmap, 0, 0, w, h);
        try { bitmap.close && bitmap.close(); } catch (_) { /* already released */ }
      } else if (video && video.videoWidth > 0) {
        ctx.drawImage(video, 0, 0, w, h);
      } else {
        toast.error("No se pudo capturar el fotograma (cámara aún cargando). Inténtelo de nuevo.");
        return;
      }
      const dataUrl = canvas.toDataURL("image/jpeg", 0.78);
      debug("[capture] OK", w, "x", h, "size~", Math.round(dataUrl.length / 1024), "KB");

      // Usamos la geolocalización CACHEADA por el watcher (siempre fresca, sin bloquear).
      // Leemos del ref para evitar closures stale en React.
      const g = geoRef.current;
      // Feedback inmediato al usuario; la subida va en background.
      toast.success(g ? "Foto capturada — enviando…" : "Foto capturada (sin ubicación) — enviando…");
      api.post(`/session/${token}/photos`, {
        image_b64: dataUrl,
        lat: g?.lat, lng: g?.lng, accuracy: g?.accuracy,
        captured_at: new Date().toISOString(),
      }).then(() => {
        toast.success("Foto enviada al perito");
      }).catch((err) => {
        console.error(err);
        toast.error("No se pudo enviar la foto al perito");
      });
    } catch (e) {
      console.error(e);
      toast.error("No se pudo capturar la foto");
    }
  };

  const startTranscription = (stream) => {
    if (!window.MediaRecorder) return;
    recordingActiveRef.current = true;
    const audioStream = new MediaStream(stream.getAudioTracks());

    // Voice Activity Detection con AnalyserNode (filtra silencio antes de mandar a Whisper)
    let audioCtx = null;
    let analyser = null;
    let dataArr = null;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(audioStream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      dataArr = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) { console.warn("[stt] AudioContext failed", e); }

    const measureRms = () => {
      if (!analyser) return 1; // si no hay analyser, no filtramos
      analyser.getByteTimeDomainData(dataArr);
      let sum = 0;
      for (let i = 0; i < dataArr.length; i++) {
        const v = (dataArr[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / dataArr.length);
    };

    const MIN_CHUNK_MS = 4000;     // chunk mínimo: <4s da fragmentos demasiado cortos a Whisper
    const MAX_CHUNK_MS = 12000;    // chunk máximo: >12s no aporta y aumenta latencia percibida
    const SILENCE_FLUSH_MS = 700;  // si tras MIN_CHUNK_MS hay 700ms de silencio → flush
    const RMS_THRESHOLD = 0.012;   // filtra ruido de fondo
    const MIN_VOICED_MS = 250;     // al menos 250ms de voz acumulada para mandar el chunk

    const tick = () => {
      if (!recordingActiveRef.current) return;
      const mime = "audio/webm";
      let maxRms = 0;
      let voicedMs = 0;
      let silenceMs = 0;
      let elapsedMs = 0;
      let rmsInterval = null;
      let chunkTimeoutId = null;
      let stopped = false;
      const stopRecorder = (rec) => {
        if (stopped) return;
        stopped = true;
        if (rmsInterval) clearInterval(rmsInterval);
        if (chunkTimeoutId) clearTimeout(chunkTimeoutId);
        try { rec.state !== "inactive" && rec.stop(); } catch (err) { console.warn("[stt] recorder stop", err); }
      };
      try {
        const r = new MediaRecorder(audioStream, { mimeType: mime });
        const chunks = [];
        r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
        r.onstop = async () => {
          try {
            const blob = new Blob(chunks, { type: mime });
            debug(`[stt] chunk size=${blob.size}B maxRms=${maxRms.toFixed(4)} voiced=${voicedMs}ms elapsed=${elapsedMs}ms`);
            if (blob.size > 2500 && maxRms >= RMS_THRESHOLD && voicedMs >= MIN_VOICED_MS) {
              const fd = new FormData();
              fd.append("audio", blob, "chunk.webm");
              fd.append("role", "asegurado");
              await fetch(`${API}/session/${token}/transcribe`, { method: "POST", body: fd });
            } else {
              debug("[stt] chunk descartado (sin voz suficiente)");
            }
          } catch (err) { console.warn("[stt] upload error", err); }
          if (recordingActiveRef.current) tick();
        };
        recorderRef.current = r;
        r.start();
        // Monitor RMS continuo: detectar voz y silencio para flushear en frontera natural
        rmsInterval = setInterval(() => {
          const rms = measureRms();
          if (rms > maxRms) maxRms = rms;
          elapsedMs += 100;
          if (rms >= RMS_THRESHOLD) {
            voicedMs += 100;
            silenceMs = 0;
          } else {
            silenceMs += 100;
          }
          // Flush por silencio prolongado tras haber tenido voz, y solo si llevamos
          // al menos el mínimo de chunk → evita cortes a mitad de palabra.
          if (
            elapsedMs >= MIN_CHUNK_MS &&
            voicedMs >= MIN_VOICED_MS &&
            silenceMs >= SILENCE_FLUSH_MS
          ) {
            stopRecorder(r);
          }
        }, 100);
        // Tope de seguridad: nunca dejar pasar más de MAX_CHUNK_MS
        chunkTimeoutId = setTimeout(() => stopRecorder(r), MAX_CHUNK_MS);
      } catch (e) {
        console.warn("MediaRecorder unsupported", e);
      }
    };
    tick();
  };

  const sendChat = () => {
    const t = chatInput.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "chat", text: t }));
    setChatInput("");
  };

  const toggleMic = () => {
    const next = !aseguradoMicOn;
    setAseguradoMicOn(next);
    const s = localStreamRef.current;
    if (s) s.getAudioTracks().forEach(t => (t.enabled = next));
  };

  const toggleTorch = async () => {
    await applyTorch(!torchOn, /* fromPerito */ false);
  };

  const applyTorchFromPerito = async (on) => {
    await applyTorch(on, /* fromPerito */ true);
  };

  // Aplica el estado de la linterna a la pista de vídeo actual y notifica al perito.
  const applyTorch = async (on, fromPerito) => {
    const s = localStreamRef.current;
    const vt = s && s.getVideoTracks()[0];
    if (!vt || typeof vt.applyConstraints !== "function") {
      if (fromPerito) toast.info("El perito ha intentado encender la linterna, pero no está disponible.");
      return;
    }
    try {
      await vt.applyConstraints({ advanced: [{ torch: !!on }] });
      setTorchOn(!!on);
      try {
        wsRef.current?.send(JSON.stringify({ type: "torch_state", supported: true, on: !!on }));
      } catch (_) { /* ignore */ }
      if (fromPerito) {
        toast(on ? "Linterna encendida por el perito" : "Linterna apagada por el perito");
      }
    } catch (e) {
      console.warn("[asegurado] torch toggle failed", e);
      if (!fromPerito) toast.error("Tu navegador no permite activar la linterna en esta llamada.");
      setTorchSupported(false);
      try {
        wsRef.current?.send(JSON.stringify({ type: "torch_state", supported: false, on: false }));
      } catch (_) { /* ignore */ }
    }
  };

  const endCall = (reason = "Has finalizado la sesión") => {
    recordingActiveRef.current = false;
    // Cleanup defensivo: si algo ya estaba cerrado, lo silenciamos a propósito.
    try { wsRef.current?.close(); } catch (_) { /* cleanup */ }
    try { pcRef.current?.close(); } catch (_) { /* cleanup */ }
    try { localStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) { /* cleanup */ }
    try { if (window.__vp_geo_watch != null) navigator.geolocation.clearWatch(window.__vp_geo_watch); } catch (_) { /* cleanup */ }
    watchdogRef.current = null;  // El watchdog deja de re-armarse al perder la referencia.
    setStep(STEP.ENDED);
    toast(reason);
  };

  useEffect(() => {
    return () => {
      recordingActiveRef.current = false;
      try { wsRef.current?.close(); } catch (_) { /* cleanup */ }
      try { pcRef.current?.close(); } catch (_) { /* cleanup */ }
      try { localStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) { /* cleanup */ }
    };
  }, []);

  // === Renders ===
  if (step === STEP.LOADING) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando…</div>;
  }
  if (error && step !== STEP.ENDED) {
    // Errores generales antes de saber qué paso mostrar (ej. token 404).
    // Si ya estamos en STEP.ENDED, la siguiente pantalla muestra el error con
    // el branding correcto (icono naranja + CTA secundario para contactar al perito).
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white">
        <AlertCircle className="h-10 w-10 text-red-500 mb-3" />
        <div className="font-heading text-xl font-bold">No es posible abrir esta sesión</div>
        <div className="text-sm text-slate-500 mt-1">{error}</div>
      </div>
    );
  }

  // Welcome + consent
  if (step === STEP.WELCOME) {
    return (
      <div className="min-h-[100svh] bg-[#1A1A1C] text-white flex flex-col overflow-hidden" data-testid="welcome-screen">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 100 100" width="32" height="32">
              <rect width="100" height="100" rx="22" fill="#D85A30"/>
              <rect x="22" y="22" width="13" height="56" fill="#FFFFFF"/>
              <polygon points="35,50 78,22 78,78" fill="#FFFFFF"/>
            </svg>
            <div>
              <div className="font-heading font-medium text-[15px] tracking-tight leading-tight"><span>eks</span><span className="text-[#D85A30]">vidi</span></div>
              <div className="text-[10px] text-white/55 leading-tight">Expediente {info.expediente_num}</div>
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-4">
            Hola, {info.asegurado_nombre.split(" ")[0]}.
          </h1>
          <p className="text-white/75 mt-1.5 text-[13px] leading-snug">
            {info.perito_name} te va a atender en directo para hacer la peritación a distancia. Solo necesitamos un par de permisos.
          </p>
        </div>
        <div className="flex-1 bg-[#FBFAF6] text-[#2C2C2A] rounded-t-[24px] px-5 pt-4 pb-4 space-y-3 flex flex-col">
          <h2 className="font-heading text-base font-semibold">Antes de empezar</h2>
          <ul className="text-[13px] space-y-2 text-[#2C2C2A] leading-snug">
            <li className="flex gap-2.5"><Camera className="h-4 w-4 text-[#D85A30] shrink-0 mt-0.5" /><span>Usaremos la <b>cámara trasera</b> de tu móvil para que el perito vea los daños.</span></li>
            <li className="flex gap-2.5"><Mic className="h-4 w-4 text-[#D85A30] shrink-0 mt-0.5" /><span>Activamos el <b>micrófono</b> para hablar con el perito y transcribir la conversación.</span></li>
            <li className="flex gap-2.5"><MapPin className="h-4 w-4 text-[#D85A30] shrink-0 mt-0.5" /><span>Pediremos tu <b>ubicación</b> para asociarla a cada fotografía.</span></li>
            <li className="flex gap-2.5"><Lock className="h-4 w-4 text-[#D85A30] shrink-0 mt-0.5" /><span>Todo se guarda solo para este expediente y de forma segura (RGPD).</span></li>
          </ul>
          <label className="flex items-start gap-2.5 bg-[#FAECE7] border border-[#E5E2D8] rounded-[12px] p-2.5 cursor-pointer" htmlFor="consent">
            <Checkbox id="consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="consent-checkbox" />
            <span className="text-[12px] text-[#2C2C2A] leading-snug">
              Acepto el aviso de privacidad y doy mi consentimiento para que se capture vídeo, fotos geolocalizadas y la transcripción de la conversación.
            </span>
          </label>
          <div className="flex-1" />
          <Button
            className="w-full btn-eks-primary rounded-[12px] h-11 text-[15px]"
            onClick={acceptConsent}
            disabled={!consent || accepting}
            data-testid="accept-consent-btn"
          >
            {accepting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Acepto y continúo
          </Button>
        </div>
      </div>
    );
  }

  if (step === STEP.PERMISSIONS) {
    return (
      <div className="min-h-[100svh] bg-[#FBFAF6] px-5 py-5 flex flex-col overflow-hidden" data-testid="permissions-screen">
        <h1 className="font-heading text-xl font-bold tracking-tight text-[#2C2C2A]">Permisos del navegador</h1>
        <p className="text-[13px] text-[#7A786F] mt-1 leading-snug">Al pulsar el botón, el móvil te pedirá permiso para cámara, micrófono y ubicación. Acéptalos para empezar.</p>
        <ul className="mt-4 space-y-2.5 text-[14px] text-[#2C2C2A]">
          <li className="flex gap-3 items-center"><Camera className="h-5 w-5 text-[#D85A30]" /> Cámara trasera</li>
          <li className="flex gap-3 items-center"><Mic className="h-5 w-5 text-[#D85A30]" /> Micrófono</li>
          <li className="flex gap-3 items-center"><MapPin className="h-5 w-5 text-[#D85A30]" /> Ubicación</li>
        </ul>
        <div className="flex-1" />
        <Button className="w-full btn-eks-primary rounded-[12px] h-11 text-[15px]" onClick={startSession} data-testid="start-call-btn">
          <Video className="h-4 w-4 mr-2" /> Iniciar videollamada
        </Button>
      </div>
    );
  }

  if (step === STEP.ENDED) {
    // Si hay un mensaje de error explícito del servidor (sesión finalizada/cancelada
    // por el perito, expediente no encontrado, etc.), lo mostramos al asegurado.
    // Si simplemente colgó él la llamada, mostramos el agradecimiento.
    const isErr = !!error;
    return (
      <div className="min-h-screen bg-[#1A1A1C] text-white flex flex-col items-center justify-center px-6 text-center">
        {isErr ? (
          <AlertCircle className="h-12 w-12 text-[#D85A30] mb-4" />
        ) : (
          <CheckCircle2 className="h-12 w-12 text-[#1D9E75] mb-4" />
        )}
        <h1 className="font-heading text-2xl font-bold">{isErr ? "Sesión cerrada" : "Sesión finalizada"}</h1>
        <p className="text-white/70 mt-2 max-w-xs">
          {isErr ? error : "Gracias por tu colaboración. El perito ya tiene todo lo que necesita."}
        </p>
        {isErr && (
          <p className="text-white/50 text-xs mt-3 max-w-xs">
            Si necesitas continuar con la peritación, contacta con el perito o con tu compañía para que te envíe un nuevo enlace.
          </p>
        )}
      </div>
    );
  }

  // === CALL VIEW ===
  return (
    <div className="vp-mobile-shell relative" data-testid="insured-call-screen">
      {/* Local video fullscreen (back camera) */}
      <video ref={localVideoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />

      {/* Top overlay */}
      <div className="absolute top-0 left-0 right-0 px-4 pt-[env(safe-area-inset-top,16px)] pt-4 flex items-center justify-between text-white">
        <ConnBadge state={connState} />
        <div className="text-xs text-white/80 font-mono bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
          {info.expediente_num}
        </div>
      </div>

      {/* Aviso si está en solo-audio: el asegurado debe saber que el perito no le ve */}
      {audioOnly && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 bg-[#7A5A1C] text-white text-[11px] font-medium px-3 py-1.5 rounded-full shadow-lg" data-testid="audio-only-banner">
          Modo solo audio · sin imagen
        </div>
      )}

      {/* Perito remote stream — el <video> SIEMPRE está montado para preservar el
          audio aunque el perito apague la cámara. Cambiamos solo la presentación. */}
      <div
        className={
          peritoCamOn
            ? "absolute top-16 right-3 w-28 aspect-[9/16] rounded-[14px] overflow-hidden border border-white/40 bg-black"
            : "absolute -left-[9999px] top-0 w-px h-px overflow-hidden"
        }
        data-testid="perito-pip"
      >
        <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
      </div>
      {!peritoCamOn && (
        <div
          className="absolute top-16 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-white/90 text-[11px] font-medium border border-white/15"
          data-testid="perito-audio-pill"
        >
          <Mic className="h-3 w-3 text-[#7FE5C4]" />
          Perito · solo audio
        </div>
      )}

      {/* Geo indicator */}
      {geo && (
        <div className="absolute bottom-32 left-3 bg-black/60 backdrop-blur text-white text-[10px] rounded-full px-3 py-1.5 flex items-center gap-1.5">
          <MapPin className="h-3 w-3 text-[#7FE5C4]" /> {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}
        </div>
      )}

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-[env(safe-area-inset-bottom,16px)] pb-5">
        <div className="flex items-center justify-around bg-black/60 backdrop-blur border border-white/10 rounded-[18px] p-2">
          <button
            onClick={toggleMic}
            className={`h-12 w-12 rounded-full flex items-center justify-center ${aseguradoMicOn ? "bg-white/10" : "bg-[#A32D2D]"} text-white`}
            data-testid="insured-mic-btn"
          >
            {aseguradoMicOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </button>
          {torchSupported && (
            <button
              onClick={toggleTorch}
              className={`h-12 w-12 rounded-full flex items-center justify-center text-white ${torchOn ? "bg-[#F2C94C] text-[#2C2C2A]" : "bg-white/10"}`}
              data-testid="insured-torch-btn"
              aria-label={torchOn ? "Apagar linterna" : "Encender linterna"}
              title={torchOn ? "Apagar linterna" : "Encender linterna"}
            >
              {torchOn ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
            </button>
          )}
          <button
            onClick={() => setChatOpen(true)}
            className="h-12 w-12 rounded-full bg-white/10 text-white flex items-center justify-center relative"
            data-testid="insured-chat-btn"
          >
            <MessageSquare className="h-5 w-5" />
            {chat.length > 0 && <span className="absolute -top-1 -right-1 bg-[#D85A30] text-white text-[10px] rounded-full px-1.5 py-0.5">{chat.length}</span>}
          </button>
          <button
            onClick={() => endCall()}
            className="h-14 w-14 rounded-full bg-[#A32D2D] text-white flex items-center justify-center"
            data-testid="insured-end-btn"
          >
            <X className="h-7 w-7" />
          </button>
        </div>
        <div className="text-[10px] text-white/70 text-center mt-2">
          {info.perito_name} te ha conectado. Mueve el móvil despacio para mostrar los daños.
        </div>
      </div>

      {/* Chat sheet */}
      {chatOpen && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-end" data-testid="insured-chat-sheet">
          <div className="w-full bg-[#FBFAF6] text-[#2C2C2A] rounded-t-[24px] max-h-[80vh] flex flex-col">
            <div className="px-5 py-3 border-b border-[#E5E2D8] flex items-center justify-between">
              <div className="font-heading font-semibold">Chat con {info.perito_name}</div>
              <button onClick={() => setChatOpen(false)} className="h-8 w-8 rounded-full bg-[#FAECE7] flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto vp-scroll p-4 space-y-2 min-h-[40vh]">
              {chat.length === 0 ? (
                <div className="text-xs text-[#7A786F] text-center py-8">Cuando escriba, aparecerá aquí.</div>
              ) : chat.map((m, i) => (
                <div key={m.id || i} className={`flex ${m.role === "asegurado" ? "justify-end" : "justify-start"}`}>
                  <div className={`px-3 py-1.5 rounded-[12px] max-w-[80%] text-sm ${m.role === "asegurado" ? "bg-[#D85A30] text-white" : "bg-white border border-[#E5E2D8] text-[#2C2C2A]"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-[#E5E2D8] flex gap-2">
              <Input
                value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Escribe un mensaje al perito…"
                data-testid="insured-chat-input"
                className="rounded-[10px] border-[#E5E2D8]"
              />
              <Button onClick={sendChat} className="btn-eks-primary rounded-[10px]" data-testid="insured-chat-send"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ children, className = "" }) {
  return <span className={`inline-flex items-center text-[10px] font-medium px-2.5 py-1 rounded-full ${className}`}>{children}</span>;
}

// Indicador visible del estado de la conexión, en la barra superior.
// Es CRÍTICO para el asegurado: si la conexión está reconectándose, debe
// saberlo para no colgar el teléfono o cerrar la app.
function ConnBadge({ state }) {
  if (state === "connecting") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#6B82C6] text-white text-[11px] font-medium" data-testid="conn-badge-connecting">
        <span className="vp-live-dot" /> Conectando…
      </span>
    );
  }
  if (state === "reconnecting") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C46A1A] text-white text-[11px] font-medium" data-testid="conn-badge-reconnecting">
        <span className="vp-live-dot" /> Reconectando…
      </span>
    );
  }
  // connected / idle
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D85A30] text-white text-[11px] font-medium" data-testid="conn-badge-live">
      <span className="vp-live-dot" /> En directo
    </span>
  );
}

